import { FC, useCallback, useEffect, useRef, useState } from "react"
import { Form, Formik, Field, FormikProps } from "formik"
import * as Yup from "yup"
import { Copy, Check } from "lucide-react"
import {
  Integration,
  EmbedTroubleshooterResult,
  EmbedTroubleshooterSubmitResponse,
  TroubleshooterFinding,
  TroubleshooterFixStep
} from "../../../../types"
import {
  useSubmitTroubleshooterMutation,
  useLazyGetTroubleshooterStatusQuery
} from "../../../../redux/queries"
import Input from "../../form/input"
import { PrimaryBtn, LightBtn } from "../../buttons"

const troubleshootSchema = Yup.object().shape({
  url: Yup.string().url("Must be a valid URL").required("URL is required"),
  problemDescription: Yup.string().required("Problem description is required")
})

type FormValues = {
  url: string
  problemDescription: string
}

type ViewState = "form" | "polling" | "results"

const SEVERITY_BORDER: Record<string, string> = {
  error: "border-l-red-500",
  warning: "border-l-yellow-500",
  info: "border-l-blue-500",
  pass: "border-l-green-500"
}

const SEVERITY_BG: Record<string, string> = {
  error: "bg-red-50",
  warning: "bg-yellow-50",
  info: "bg-blue-50",
  pass: "bg-green-50"
}

const STATUS_PILL: Record<string, string> = {
  critical: "bg-red-100 text-red-800",
  warning: "bg-yellow-100 text-yellow-800",
  pass: "bg-green-100 text-green-800"
}

const PRIORITY_BADGE: Record<string, string> = {
  critical: "bg-red-100 text-red-800",
  recommended: "bg-yellow-100 text-yellow-800",
  "best-practice": "bg-blue-100 text-blue-800"
}

interface Props {
  integration: Integration
}

const getStorageKey = (integrationId: string) =>
  `troubleshooter-${integrationId}`

const formatResultsAsText = (result: EmbedTroubleshooterResult): string => {
  const r = result.results
  const lines: string[] = ["Embed Troubleshooter Report", ""]

  if (result.url) lines.push(`URL: ${result.url}`)
  if (result.problemDescription)
    lines.push(`Problem: ${result.problemDescription}`)
  if (r?.timestamp) lines.push(`Date: ${r.timestamp}`)
  lines.push(`Status: ${result.status}`)
  lines.push("")

  if (r?.summary) {
    lines.push(
      `Overall: ${r.summary.overallStatus} — ${r.summary.errors} error(s), ${r.summary.warnings} warning(s), ${r.summary.passes} passed`
    )
    lines.push("")
  }

  if (r?.diagnosis) {
    lines.push("Diagnosis:", r.diagnosis, "")
  }

  if (r?.fixSteps && r.fixSteps.length > 0) {
    lines.push("Fix Steps:")
    r.fixSteps.forEach((step, i) => {
      lines.push(`  ${i + 1}. [${step.priority}] ${step.step}`)
    })
    lines.push("")
  }

  if (r?.findings && r.findings.length > 0) {
    lines.push("Findings:")
    r.findings.forEach(f => {
      lines.push(`  [${f.severity}] ${f.title}`)
      lines.push(`    ${f.detail}`)
      if (f.suggestion) lines.push(`    Suggestion: ${f.suggestion}`)
      if (f.evidence) lines.push(`    Evidence: ${f.evidence}`)
    })
    lines.push("")
  }

  return lines.join("\n")
}

const Troubleshoot: FC<Props> = ({ integration }) => {
  const [viewState, setViewState] = useState<ViewState>("form")
  const [result, setResult] = useState<EmbedTroubleshooterResult | null>(null)
  const [copied, setCopied] = useState(false)
  const [submitTroubleshooter] = useSubmitTroubleshooterMutation()
  const [getTroubleshooterStatus] = useLazyGetTroubleshooterStatusQuery()
  const pollingRef = useRef<ReturnType<typeof setInterval> | null>(null)
  const jobIdRef = useRef<string | null>(null)

  const stopPolling = useCallback(() => {
    if (pollingRef.current) {
      clearInterval(pollingRef.current)
      pollingRef.current = null
    }
  }, [])

  useEffect(() => {
    return () => stopPolling()
  }, [stopPolling])

  useEffect(() => {
    const storedJobId = localStorage.getItem(getStorageKey(integration.id))
    if (!storedJobId) return

    getTroubleshooterStatus(storedJobId)
      .unwrap()
      .then(res => {
        if (res.status === "complete" || res.status === "error") {
          setResult(res)
          setViewState("results")
        } else {
          pollForResult(storedJobId)
        }
      })
      .catch(() => {
        localStorage.removeItem(getStorageKey(integration.id))
      })
  }, [integration.id]) // eslint-disable-line react-hooks/exhaustive-deps

  const pollForResult = useCallback(
    (id: string) => {
      jobIdRef.current = id
      setViewState("polling")

      pollingRef.current = setInterval(async () => {
        try {
          const res = await getTroubleshooterStatus(id).unwrap()
          if (res.status === "complete" || res.status === "error") {
            stopPolling()
            setResult(res)
            setViewState("results")
          }
        } catch {
          stopPolling()
          setViewState("results")
        }
      }, 3000)
    },
    [getTroubleshooterStatus, stopPolling]
  )

  const handleSubmit = async (values: FormValues) => {
    try {
      const res = (await submitTroubleshooter(
        values
      ).unwrap()) as EmbedTroubleshooterSubmitResponse
      if (res.id) {
        localStorage.setItem(getStorageKey(integration.id), res.id)
        pollForResult(res.id)
      }
    } catch {
      // submission failed, stay on form
    }
  }

  const handleRunAgain = () => {
    stopPolling()
    localStorage.removeItem(getStorageKey(integration.id))
    setResult(null)
    setViewState("form")
  }

  const handleCopy = () => {
    if (!result) return
    navigator.clipboard.writeText(formatResultsAsText(result)).then(() => {
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    })
  }

  if (viewState === "form") {
    return (
      <div className="p-4">
        <Formik
          initialValues={{
            url: integration.url || "",
            problemDescription: ""
          }}
          validationSchema={troubleshootSchema}
          onSubmit={handleSubmit}
        >
          {({ isSubmitting }: FormikProps<FormValues>) => (
            <Form>
              <Input
                label="URL"
                name="url"
                placeholder="https://example.com/page"
                className="mb-4"
              />
              <div className="mb-4">
                <label
                  htmlFor="problemDescription"
                  className="block text-sm font-medium text-gray-700 mb-1"
                >
                  Problem Description
                </label>
                <Field
                  as="textarea"
                  id="problemDescription"
                  name="problemDescription"
                  rows={4}
                  placeholder="Describe the issue you're experiencing with the embed..."
                  className="appearance-none block w-full px-3 py-2.5 border border-neutral-200 rounded-md placeholder-gray-400 focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                />
              </div>
              <PrimaryBtn type="submit" disabled={isSubmitting}>
                Run Diagnostics
              </PrimaryBtn>
            </Form>
          )}
        </Formik>
      </div>
    )
  }

  if (viewState === "polling") {
    return (
      <div className="p-4 flex flex-col items-center justify-center min-h-[200px]">
        <div className="flex items-center gap-3">
          <span className="relative flex h-3 w-3">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-primary-400 opacity-75" />
            <span className="relative inline-flex rounded-full h-3 w-3 bg-primary-500" />
          </span>
          <span className="text-gray-600 text-sm">Analyzing embed...</span>
        </div>
      </div>
    )
  }

  // Results view
  const results = result?.results
  const summary = results?.summary

  if (result?.status === "error" && !results) {
    return (
      <div className="p-4">
        <div className="bg-red-50 border border-red-200 rounded-md p-4 mb-4">
          <p className="text-red-800 text-sm font-medium">
            Diagnostics failed. Please try again.
          </p>
        </div>
        <LightBtn onClick={handleRunAgain}>Run Again</LightBtn>
      </div>
    )
  }

  return (
    <div className="p-4 space-y-4">
      {/* Header: overall status + duration */}
      {summary && (
        <div className="flex items-center justify-between">
          <span
            className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
              STATUS_PILL[summary.overallStatus] || STATUS_PILL.warning
            }`}
          >
            {summary.overallStatus}
          </span>
          {results?.duration != null && (
            <span className="text-xs text-gray-500">
              {(results.duration / 1000).toFixed(1)}s
            </span>
          )}
        </div>
      )}

      {/* Diagnosis */}
      {results?.diagnosis && (
        <div className="text-sm text-gray-700">{results.diagnosis}</div>
      )}

      {/* Summary counters */}
      {summary && (
        <div className="flex gap-3 text-xs">
          <span className="px-2 py-1 bg-red-50 text-red-700 rounded">
            {summary.errors} error{summary.errors !== 1 ? "s" : ""}
          </span>
          <span className="px-2 py-1 bg-yellow-50 text-yellow-700 rounded">
            {summary.warnings} warning{summary.warnings !== 1 ? "s" : ""}
          </span>
          <span className="px-2 py-1 bg-green-50 text-green-700 rounded">
            {summary.passes} passed
          </span>
        </div>
      )}

      {/* Fix steps */}
      {results?.fixSteps && results.fixSteps.length > 0 && (
        <div>
          <h4 className="text-sm font-medium text-gray-900 mb-2">Fix Steps</h4>
          <ol className="space-y-2">
            {results.fixSteps.map((step: TroubleshooterFixStep, i: number) => (
              <li key={i} className="flex items-start gap-2 text-sm">
                <span
                  className={`inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium shrink-0 ${
                    PRIORITY_BADGE[step.priority] || ""
                  }`}
                >
                  {step.priority}
                </span>
                <span className="text-gray-700">{step.step}</span>
              </li>
            ))}
          </ol>
        </div>
      )}

      {/* Findings */}
      {results?.findings && results.findings.length > 0 && (
        <div>
          <h4 className="text-sm font-medium text-gray-900 mb-2">Findings</h4>
          <div className="space-y-2">
            {results.findings.map(
              (finding: TroubleshooterFinding, i: number) => (
                <details
                  key={finding.checkId || i}
                  className={`border-l-4 ${
                    SEVERITY_BORDER[finding.severity] || ""
                  } ${SEVERITY_BG[finding.severity] || ""} rounded-r-md`}
                >
                  <summary className="px-3 py-2 cursor-pointer text-sm font-medium text-gray-800">
                    {finding.title}
                  </summary>
                  <div className="px-3 pb-3 text-sm space-y-1">
                    <p className="text-gray-600">{finding.detail}</p>
                    {finding.suggestion && (
                      <p className="text-gray-700">
                        <span className="font-medium">Suggestion:</span>{" "}
                        {finding.suggestion}
                      </p>
                    )}
                    {finding.evidence && (
                      <pre className="text-xs bg-white/50 p-2 rounded overflow-x-auto">
                        {finding.evidence}
                      </pre>
                    )}
                  </div>
                </details>
              )
            )}
          </div>
        </div>
      )}

      {/* Metadata */}
      {results?.metadata && Object.keys(results.metadata).length > 0 && (
        <div className="text-xs text-gray-500">
          {results.metadata.cms && <span>CMS: {results.metadata.cms}</span>}
          {results.metadata.cms && results.metadata.platform && (
            <span> | </span>
          )}
          {results.metadata.platform && (
            <span>Platform: {results.metadata.platform}</span>
          )}
        </div>
      )}

      <div className="flex gap-2">
        <LightBtn onClick={handleRunAgain}>Run Again</LightBtn>
        <LightBtn onClick={handleCopy}>
          {copied ? (
            <Check className="inline h-4 w-4 mr-1" />
          ) : (
            <Copy className="inline h-4 w-4 mr-1" />
          )}
          {copied ? "Copied" : "Copy Results"}
        </LightBtn>
      </div>
    </div>
  )
}

export default Troubleshoot
